import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Formik, FormikErrors, FormikProps } from 'formik';
import { Button, LinkButton, RadioButton } from '@pmi/dsm-react';
import { useParams } from 'react-router';
import styles from '../cart.module.scss';
import {
  useAddProfileAddressMutation,
  useGetProfileAddressesQuery,
  useSetCartBillingAddressMutation,
  useUpdateProfileAddressMutation,
} from '../apiSlice';
import AddressForm from './AddressForm';
import { Address } from '../../../models/ProfileModel';
import { toastWithAnalytics } from '../../../analytics';
import useSchemaValidator from '../../../hooks/useSchemaValidator';
import errorFromResponse from '../../../utils/extractErrorFromResponse';
import { useStatesQueryMutation } from '../../../../Hub/TrainingHub/EnrollmentCard/ProductModal/apiSlice';
import { indiaErrorHandler } from '../../../utils/indiaErrorHandler';

export interface BillingAddressProps {
  onBillingAddressSelected: (address: Address, init: boolean) => void;
  onNoAddressAvailable: (hasNoAddress: boolean) => void;
  cartBillingAddress: Address | null;
  setCartAddressError: Dispatch<SetStateAction<boolean>>;
  isCartProductsLoading: boolean;
  setShowIndiaError: (value: string) => void;
  isProcessing: boolean;
  setIsProcessing: (value: boolean) => void;
}

export default function BillingAddress({
  onBillingAddressSelected,
  cartBillingAddress,
  onNoAddressAvailable,
  setCartAddressError,
  isCartProductsLoading,
  setShowIndiaError,
  isProcessing,
  setIsProcessing,
}: BillingAddressProps) {
  const { accountId } = useParams<{ accountId: string }>();
  const [state, setState] = useState<'add' | 'edit' | 'default'>('default');
  const { data, isLoading, isFetching } = useGetProfileAddressesQuery(null);
  const countries = data?._embedded?.countries || [];
  const [addresses, setAddresses] = useState<Address[]>([]);
  const emptyAddress = {
    address1: '',
    address2: '',
    address3: '',
    state: '',
    city: '',
    country: '',
    primary: true,
    postalCode: '',
    id: 0,
  };
  const [currentAddress, setCurrentAddress] = useState<Address>(
    cartBillingAddress ?? emptyAddress,
  );
  const [selectedAddressToEdit, setSelectedAddressToEdit] =
    useState<Address | null>(cartBillingAddress);
  const formikRef = useRef<FormikProps<Address>>(undefined);
  const { validateAddress } = useSchemaValidator();

  const [addProfileAddress, addedProfileAddress] =
    useAddProfileAddressMutation();
  const [updateProfileAddress, updatedProfileAddress] =
    useUpdateProfileAddressMutation();
  const [setBillingAddress, settedBillingAddress] =
    useSetCartBillingAddressMutation();

  const [statesQuery, { isLoading: statesLoading }] = useStatesQueryMutation();
  const [stateStateIsLoading, setStateStateIsLoading] = useState(false);

  const validateBillingAddress = (values: Address) => {
    let errors: FormikErrors<Address> = {};
    try {
      const result = validateAddress({ ...values }, data?._embedded?.schema);
      if (!result.valid) {
        errors = result.errors;
      }
    } catch (error) {
      toastWithAnalytics('Unexpected Error while validating data', 'danger');
    }
    setTimeout(() => {
      const invalids = document.querySelectorAll(
        '.mdlProfileUpdate .is-invalid',
      );
      if (invalids.length > 0) {
        invalids.item(0).scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
    return errors;
  };

  const setCartBillingAddress = async (address: Address) => {
    setStateStateIsLoading(true);
    // reset error when user selects new address
    setShowIndiaError(null);

    try {
      await setBillingAddress({ address, accountId }).unwrap();
      settedBillingAddress.reset();

      setCurrentAddress(address);
      onBillingAddressSelected(address, false);
    } catch (err: any) {
      const { errorMessage, isIndiaError } = indiaErrorHandler(err);

      if (isIndiaError && errorMessage) {
        setShowIndiaError(errorMessage);
        return;
      }

      if (err?.data && err?.data?.Value) {
        errorFromResponse(err?.data);
      } else {
        toastWithAnalytics('Something went wrong. Please try again.', 'danger');
      }
    } finally {
      setState('default');
      setIsProcessing(false);
      setStateStateIsLoading(false);
    }
  };

  const onAddressUseThisNewOrEditedAddress = async () => {
    setIsProcessing(true);
    // reset error when user selects new address
    setShowIndiaError(null);
    await formikRef.current.submitForm();
    if (Object.values(formikRef.current.errors).length > 0) {
      setIsProcessing(false);
      return;
    }
    const values: Address = formikRef.current.values as Address;

    const sendAddress = {
      id: values.id,
      country: values.country,
      address1: values.address1,
      address2: values.address2,
      city: values.city,
      postalCode: values.postalCode,
      state: values.state,
      primary: values.primary,
    };

    try {
      const mutation =
        sendAddress.id > 0 ? updateProfileAddress : addProfileAddress;
      const mutated =
        sendAddress.id > 0 ? updatedProfileAddress : addedProfileAddress;

      await setBillingAddress({ address: sendAddress, accountId }).unwrap();
      settedBillingAddress.reset();

      await mutation(sendAddress).unwrap();

      setCurrentAddress(sendAddress);
      onBillingAddressSelected(sendAddress, false);

      mutated.reset();
    } catch (error) {
      const { errorMessage, isIndiaError } = indiaErrorHandler(error);

      if (isIndiaError && errorMessage) {
        setShowIndiaError(errorMessage);
        return;
      }

      toastWithAnalytics(
        'There was an unexpected error while updating the cart billing address',
        'danger',
      );
    } finally {
      setIsProcessing(false);
      setState('default');
      setIsProcessing(false);
      setStateStateIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchProfileAddresses = async () => {
      setStateStateIsLoading(true);
      setCartAddressError(false);
      onNoAddressAvailable(false);

      if (!data || !countries) {
        setAddresses([]);
        setStateStateIsLoading(false);
        return;
      }

      if (!isLoading && !isFetching) {
        const items = data.items || [];
        if (items.length === 0) {
          onNoAddressAvailable(true);
          setAddresses([]);
          setStateStateIsLoading(false);
          return;
        }

        const profileAddresses = await Promise.all(
          items.map(async (addr) => {
            try {
              const states = await statesQuery({
                country: addr.country,
              }).unwrap();
              const found = states.find(
                (s) =>
                  s.text.toLowerCase() === addr.state.toLowerCase() ||
                  s.value.toLowerCase() === addr.state.toLowerCase(),
              );
              return {
                ...addr,
                state: found ? found.value : addr.state,
                stateLabel: found ? found.text : addr.state,
              };
            } catch {
              return { ...addr, stateLabel: addr.state };
            }
          }),
        );

        setAddresses(profileAddresses);

        let selected: Address | null = null;
        if (!currentAddress?.id || currentAddress.id === 0) {
          if (cartBillingAddress) {
            selected = profileAddresses.find(
              (add) =>
                add.country.toLowerCase().trim() ===
                  cartBillingAddress.country.toLowerCase().trim() &&
                add.address1.trim() === cartBillingAddress.address1.trim() &&
                add.state.trim() === cartBillingAddress.state.trim() &&
                add.postalCode.trim() ===
                  cartBillingAddress.postalCode.trim() &&
                add.city.trim() === cartBillingAddress.city.trim(),
            );
            setCartAddressError(
              !isLoading && !isCartProductsLoading && !selected,
            );
          } else {
            selected =
              profileAddresses.find((add) => add.primary) ?? emptyAddress;
            if (selected.id && selected.id > 0) {
              await setCartBillingAddress(selected);
            }
          }
          setCurrentAddress(selected);
          onBillingAddressSelected(selected, true);
        } else {
          setCurrentAddress(currentAddress);
        }
      }

      setStateStateIsLoading(false);
    };

    fetchProfileAddresses();
  }, [data?.items, cartBillingAddress, isLoading, isFetching]);

  if (
    isLoading ||
    isFetching ||
    statesLoading ||
    stateStateIsLoading ||
    isCartProductsLoading
  ) {
    return <p>Loading...</p>;
  }

  if (
    (data?.items?.length === 0 && countries.length > 0) ||
    ['edit', 'add'].includes(state)
  ) {
    formikRef?.current?.setFieldValue('primary', true);
    return (
      <div className="form-address" id="form-address">
        <Formik
          initialValues={
            state === 'edit' ? selectedAddressToEdit : emptyAddress
          }
          onSubmit={(values) => {
            setCurrentAddress(values);
          }}
          validate={validateBillingAddress}
          validateOnBlur={false}
          validateOnChange={false}
          innerRef={formikRef}
        >
          <div className={styles.cartBillingAddress}>
            <div className="d-block mb-3">
              <h5 className="mb-3">
                {state === 'add' || addresses.length === 0 ? 'Add' : 'Edit'}{' '}
                Billing Address
              </h5>
            </div>
            <div className="d-block">
              <AddressForm
                countries={data?._embedded.countries}
                disabled={isProcessing}
                onLoading={(loading: boolean) => setIsProcessing(loading)}
                emptyAddress={data?.items?.length === 0}
              />
            </div>
            <div className="d-block" id="form-address-actions">
              <div className="form-address-actions d-flex justify-content-end align-items-center align-content-center gap-3">
                {addresses.length > 0 && (
                  <Button
                    titleText="Cancel"
                    type="button"
                    variant="outline-secondary"
                    disabled={isProcessing}
                    size="sm"
                    onClick={() => setState('default')}
                  />
                )}
                <Button
                  titleText="Use This Address"
                  disabled={isProcessing}
                  onClick={onAddressUseThisNewOrEditedAddress}
                  size="sm"
                />
              </div>
            </div>
          </div>
        </Formik>
      </div>
    );
  }

  return (
    <div className={styles.cartBillingAddress}>
      {addresses &&
        addresses.map((addr, i: number) => (
          <div
            key={`address-${addr.id}-${i.toString()}`}
            className={`${styles.addressSelection} ${
              currentAddress?.id === addr.id
                ? styles.addressSelectionActive
                : ''
            } mb-3`}
          >
            <RadioButton
              name="billing-address"
              value={addr.id}
              id={`billing-address-${addr.id}`}
              onChange={() => {
                setCurrentAddress(addr);
              }}
              checked={currentAddress?.id === addr.id}
              label={
                <span className="d-flex address-details">
                  <span className="d-block">
                    <span className="address d-block">{addr?.address1}</span>
                    <span className="state d-block">
                      {addr?.city}, {addr?.stateLabel} {addr?.postalCode}{' '}
                      {addr?.country}
                    </span>
                  </span>
                </span>
              }
            />
            <LinkButton
              titleText="Edit"
              className={`text-decoration-underline ${styles.addressSelectionEdit}`}
              onClick={() => {
                setSelectedAddressToEdit(addr);
                setState('edit');
              }}
            />
          </div>
        ))}

      <div
        id="b-address-action"
        className="b-address-action d-flex justify-content-end align-items-center align-content-center gap-3"
      >
        <Button
          titleText="Add New Address"
          type="button"
          variant="outline-secondary"
          size="sm"
          onClick={() => {
            setState('add');

            // reset the error when user selects new address
            setShowIndiaError(null);
          }}
          disabled={isProcessing}
        />
        <Button
          titleText="Use This Address"
          disabled={addresses.length === 0 || isProcessing}
          size="sm"
          onClick={() => {
            setIsProcessing(true);
            setCartBillingAddress(currentAddress);
          }}
        />
      </div>
    </div>
  );
}
